import { useForm, SubmitHandler } from "react-hook-form";
import { supabase } from "../supabaseClient";
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Eye, EyeOff, AlertCircle, CheckCircle2 } from "lucide-react";
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import i18n from "../i18n";

// Icônes typées
const CheckIcon = FaCheckCircle as unknown as React.FC<{ className?: string }>;
const CrossIcon = FaTimesCircle as unknown as React.FC<{ className?: string }>;

// Mini-hook i18n
function useI18nSafe() {
  const [lng, setLng] = useState(
    (i18n.resolvedLanguage || i18n.language || "fr").toLowerCase()
  );
  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);
  const t = (key: string, options?: Record<string, unknown>) =>
    String((i18n.t as any)(key, options));
  return { t, lng };
}

type FormInputs = {
  email: string;
  password: string;
  confirmPassword: string;
};

export const Signup = () => {
  const { t } = useI18nSafe();
  const location = useLocation();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<FormInputs>();

  const [submitted, setSubmitted] = useState(false);
  const [status, setStatus] = useState<"idle" | "ok" | "err">("idle");
  const [customError, setCustomError] = useState<string>("");
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);

  const password = watch("password") || "";
  const confirmPassword = watch("confirmPassword") || "";
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  const params = new URLSearchParams(location.search);
  const redirect = params.get("redirect") || "/";

  // Sécurise un minimum pour éviter les redirects externes
  const safeRedirect = redirect.startsWith("/") ? redirect : "/";

  const strength = (() => {
    let s = 0;
    if (password.length >= 8) s++;
    if (/[A-Z]/.test(password)) s++;
    if (/\d/.test(password)) s++;
    if (/[^A-Za-z0-9]/.test(password)) s++;
    return s;
  })();

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    setStatus("idle");
    setCustomError("");

    if (!passwordsMatch) {
      setStatus("err");
      setCustomError(t("signup:fields.confirm.mismatch"));
      return;
    }

    const emailRedirectTo = `${window.location.origin}/auth/callback?redirect=${encodeURIComponent(safeRedirect)}`;
    console.log("emailRedirectTo =", emailRedirectTo);

    const { data: signUpData, error } = await supabase.auth.signUp({
      email: data.email,
      password: data.password,
      options: {
        emailRedirectTo,
      },
    });

    if (error) {
      setStatus("err");
      setCustomError(error.message || t("signup:status.err"));
      return;
    }

    setStatus("ok");
    setSubmitted(true);
    reset({ email: "", password: "", confirmPassword: "" });

    // Si session immédiate (selon config Supabase), on redirige direct
    if (signUpData.session) {
      navigate(safeRedirect);
      return;
    }

    // Sinon, l’utilisateur devra confirmer son email puis reviendra via emailRedirectTo
  };

  return (
    <section className="min-h-screen flex items-center justify-center p-6 bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100 transition-colors">
      <div className="w-full max-w-md bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl shadow-sm p-6">
        <h1 className="text-2xl font-bold text-orange-600 dark:text-orange-400 mb-6 text-center">
          {t("signup:title")}
        </h1>

        {safeRedirect !== "/" && (
          <div className="mb-4 rounded-lg border border-orange-200 bg-orange-50 px-3 py-2 text-sm text-orange-700 dark:border-orange-900 dark:bg-orange-900/20 dark:text-orange-300">
            Après création du compte, tu seras renvoyé vers ton invitation.
          </div>
        )}

        {status !== "idle" && (
          <div
            className={`mb-4 rounded-lg border p-3 text-sm ${
              status === "ok"
                ? "border-green-200 bg-green-50 text-green-700 dark:border-green-900 dark:bg-green-900/20 dark:text-green-300"
                : "border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-900/20 dark:text-red-300"
            }`}
            role="status"
            aria-live="polite"
          >
            {status === "ok" ? (
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                {safeRedirect !== "/"
                  ? "Compte créé. Vérifie ton email puis reviens à ton invitation."
                  : t("signup:status.ok")}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {customError || t("signup:status.err")}
              </div>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1" htmlFor="email">
              {t("signup:fields.email.label")}
            </label>
            <input
              {...register("email", {
                required: { value: true, message: t("signup:fields.email.required") },
              })}
              id="email"
              className="border border-gray-300 dark:border-gray-700 rounded-lg w-full p-3
                         bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100
                         placeholder-gray-400 dark:placeholder-gray-500
                         focus:outline-none focus:ring-2 focus:ring-orange-400"
              type="email"
              placeholder={t("signup:fields.email.placeholder")}
              aria-invalid={!!errors.email || undefined}
              aria-describedby={errors.email ? "email-error" : undefined}
            />
            {errors.email && (
              <span id="email-error" className="text-red-500 text-sm">
                {errors.email.message as string}
              </span>
            )}
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-1" htmlFor="password">
              {t("signup:fields.password.label")}
            </label>
            <div className="relative">
              <input
                {...register("password", {
                  required: { value: true, message: t("signup:fields.password.required") },
                  minLength: { value: 8, message: t("signup:fields.password.minLength") },
                  pattern: {
                    value: /^(?=.*[A-Z])(?=.*\d).+$/,
                    message: t("signup:fields.password.pattern"),
                  },
                })}
                id="password"
                className="border border-gray-300 dark:border-gray-700 rounded-lg w-full p-3 pr-11
                           bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100
                           placeholder-gray-400 dark:placeholder-gray-500
                           focus:outline-none focus:ring-2 focus:ring-orange-400"
                type={showPwd ? "text" : "password"}
                placeholder={t("signup:fields.password.placeholder")}
                aria-invalid={!!errors.password || undefined}
                aria-describedby={errors.password ? "password-error" : undefined}
              />
              <button
                type="button"
                onClick={() => setShowPwd((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                aria-label={showPwd ? t("signup:aria.hidePassword") : t("signup:aria.showPassword")}
              >
                {showPwd ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {errors.password && (
              <span id="password-error" className="text-red-500 text-sm">
                {errors.password.message as string}
              </span>
            )}

            <div className="mt-2">
              <div className="h-2 w-full bg-gray-200 dark:bg-gray-800 rounded" aria-hidden />
              <div
                className={`h-2 -mt-2 rounded transition-all ${
                  strength <= 1
                    ? "bg-red-500"
                    : strength === 2
                    ? "bg-yellow-500"
                    : strength === 3
                    ? "bg-lime-500"
                    : "bg-green-600"
                }`}
                style={{ width: `${(strength / 4) * 100}%` }}
                aria-label={t("signup:strength.label")}
                aria-valuenow={strength}
                aria-valuemin={0}
                aria-valuemax={4}
                role="progressbar"
              />
              <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                {t(`signup:strength.${strength}`)}
              </div>
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium mb-1" htmlFor="confirmPassword">
              {t("signup:fields.confirm.label")}
            </label>
            <div className="relative">
              <input
                {...register("confirmPassword", {
                  required: { value: true, message: t("signup:fields.confirm.required") },
                  validate: (value) => value === password || t("signup:fields.confirm.mismatch"),
                })}
                id="confirmPassword"
                className="border border-gray-300 dark:border-gray-700 rounded-lg w-full p-3 pr-11
                           bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100
                           placeholder-gray-400 dark:placeholder-gray-500
                           focus:outline-none focus:ring-2 focus:ring-orange-400"
                type={showConfirmPwd ? "text" : "password"}
                placeholder={t("signup:fields.confirm.placeholder")}
                aria-invalid={!!errors.confirmPassword || undefined}
                aria-describedby={errors.confirmPassword ? "confirm-error" : undefined}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPwd((v) => !v)}
                className="absolute right-9 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                aria-label={showConfirmPwd ? t("signup:aria.hidePassword") : t("signup:aria.showPassword")}
              >
                {showConfirmPwd ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>

              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-lg">
                {confirmPassword ? (
                  passwordsMatch ? (
                    <CheckIcon className="text-green-500" />
                  ) : (
                    <CrossIcon className="text-red-500" />
                  )
                ) : null}
              </span>
            </div>
            {errors.confirmPassword && (
              <span id="confirm-error" className="text-red-500 text-sm block mt-1">
                {errors.confirmPassword.message as string}
              </span>
            )}
          </div>

          <div className="flex justify-center">
            <button
              type="submit"
              disabled={submitted || isSubmitting}
              className={`${
                submitted
                  ? "bg-gray-400 dark:bg-gray-700 cursor-not-allowed"
                  : "bg-orange-600 hover:bg-orange-700"
              } text-white font-semibold py-3 px-6 rounded-lg transition-colors
                 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400`}
            >
              {isSubmitting ? t("signup:cta.loading") : t("signup:cta.submit")}
            </button>
          </div>
        </form>

        <p className="mt-6 text-xs text-center text-gray-600 dark:text-gray-400">
          {t("signup:termsLine")}
        </p>
      </div>
    </section>
  );
};

export default Signup;